import { siteTexts } from "../../../shared/i18n/siteTexts";
import type {
  ChallengeDetail,
  ChallengeType,
} from "../../challenges/types/challengeAdmin";
import type { DraftPair, DraftSide } from "../../challenges/types/challengeDraft";
import {
  normalizePocoCourtsCount,
  POCO_MAX_PAIRS,
} from "../../challenges/utils/pocoSchedule";
import {
  normalizeTournamentRegistrationCategoriesForEdit,
  normalizeTournamentShirtSizes,
} from "./tournamentCategories";

export const MIN_EVENT_AMOUNT_CENTS = 500;

export function parseAmountToCents(value: string) {
  const raw = String(value || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[^\d,.-]/g, "");
  if (!raw) return undefined;

  const isNegative = raw.startsWith("-");
  const unsigned = raw.replace(/-/g, "");
  const lastComma = unsigned.lastIndexOf(",");
  const lastDot = unsigned.lastIndexOf(".");
  const decimalSeparator =
    lastComma >= 0 && lastDot >= 0
      ? lastComma > lastDot
        ? ","
        : "."
      : lastComma >= 0
        ? ","
        : lastDot >= 0
          ? "."
          : "";

  let integerDigits = "";
  let decimalDigits = "";

  if (decimalSeparator) {
    const decimalIndex = unsigned.lastIndexOf(decimalSeparator);
    const fractionalCandidate = unsigned
      .slice(decimalIndex + 1)
      .replace(/\D/g, "");
    const separatorIsDecimal = fractionalCandidate.length > 0 &&
      fractionalCandidate.length <= 2;

    if (separatorIsDecimal) {
      integerDigits = unsigned.slice(0, decimalIndex).replace(/\D/g, "");
      decimalDigits = fractionalCandidate.padEnd(2, "0").slice(0, 2);
    } else {
      integerDigits = unsigned.replace(/\D/g, "");
      decimalDigits = "00";
    }
  } else {
    integerDigits = unsigned.replace(/\D/g, "");
    decimalDigits = "00";
  }

  if (!integerDigits && !decimalDigits) return undefined;
  const reais = Number(integerDigits || "0");
  const cents = Number(decimalDigits || "0");
  if (!Number.isSafeInteger(reais) || !Number.isSafeInteger(cents)) {
    return undefined;
  }

  const totalCents = reais * 100 + cents;
  return isNegative ? 0 : totalCents;
}

export function minimumParticipantsByType(type: ChallengeType) {
  if (type === "super8") return 8;
  if (type === "poco") return 12;
  return 12;
}

export function isFixedPairOnlyChallengeType(
  type: ChallengeType | string | null | undefined,
) {
  return (
    String(type || "")
      .trim()
      .toLowerCase() === "poco"
  );
}

export function limitPairsForChallengeType(
  pairs: DraftPair[],
  challengeType: ChallengeType | string | null | undefined,
) {
  if (!isFixedPairOnlyChallengeType(challengeType)) return pairs;
  return pairs.slice(0, POCO_MAX_PAIRS);
}

export function buildInternalEventIdByTournament(tournamentId: number) {
  if (!Number.isFinite(tournamentId) || tournamentId <= 0) return "";
  return `ev_t_${Math.floor(tournamentId)}`;
}

export function resolveChallengeEventId(input?: {
  id?: number | null;
  eventId?: string | null;
}) {
  const eventId = String(input?.eventId || "").trim();
  if (eventId) return eventId;
  const tournamentId = Number(input?.id || 0);
  return buildInternalEventIdByTournament(tournamentId);
}

export function parseRoundsInput(value: string) {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch (_err) {
    return null;
  }
}

export function formatSideLabel(side: DraftSide) {
  if (side?.preferenceMissing) {
    return siteTexts.challengeEngine.labels.sidePreferenceMissing;
  }
  const right = Boolean(side?.direita);
  const left = Boolean(side?.esquerda);
  if (right && left) return siteTexts.challengeEngine.labels.rightAndLeft;
  if (right) return siteTexts.challengeEngine.labels.right;
  if (left) return siteTexts.challengeEngine.labels.left;
  return siteTexts.challengeEngine.labels.undefined;
}

export function normalizeParticipantCpfs(value: unknown) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item || "").trim()).filter(Boolean);
}

export function normalizeChallengeDetail(
  detail: ChallengeDetail,
): ChallengeDetail {
  const participantCpfs = normalizeParticipantCpfs(detail.participantCpfs);
  const categoryGender =
    detail.categoryGender === "male" ||
    detail.categoryGender === "female" ||
    detail.categoryGender === "mixed" ||
    detail.categoryGender === "open"
      ? detail.categoryGender
      : detail.challengeType === "super8"
        ? "mixed"
        : null;
  const category =
    detail.category === "7a" ||
    detail.category === "6a" ||
    detail.category === "5a" ||
    detail.category === "4a" ||
    detail.category === "3a" ||
    detail.category === "2a" ||
    detail.category === "open" ||
    detail.category === "free"
      ? detail.category
      : null;
  const categoryMale =
    detail.categoryMale === "7a" ||
    detail.categoryMale === "6a" ||
    detail.categoryMale === "5a" ||
    detail.categoryMale === "4a" ||
    detail.categoryMale === "3a" ||
    detail.categoryMale === "2a" ||
    detail.categoryMale === "open" ||
    detail.categoryMale === "free"
      ? detail.categoryMale
      : detail.challengeType === "super8"
        ? "6a"
        : null;
  const categoryFemale =
    detail.categoryFemale === "7a" ||
    detail.categoryFemale === "6a" ||
    detail.categoryFemale === "5a" ||
    detail.categoryFemale === "4a" ||
    detail.categoryFemale === "3a" ||
    detail.categoryFemale === "2a" ||
    detail.categoryFemale === "open" ||
    detail.categoryFemale === "free"
      ? detail.categoryFemale
      : detail.challengeType === "super8"
        ? "6a"
        : null;
  const super8Group =
    detail.super8Group === "A" || detail.super8Group === "B"
      ? detail.super8Group
      : null;
  const includeInRanking =
    String(detail.challengeType || "")
      .trim()
      .toLowerCase() === "tournament"
      ? Boolean(detail.includeInRanking ?? false)
      : Boolean(detail.includeInRanking ?? true);
  const registrationCategories =
    String(detail.challengeType || "")
      .trim()
      .toLowerCase() === "tournament"
      ? normalizeTournamentRegistrationCategoriesForEdit(
          detail.registrationCategories,
        )
      : Array.isArray(detail.registrationCategories)
        ? detail.registrationCategories
        : [];
  const isPoco =
    String(detail.challengeType || "").trim().toLowerCase() === "poco";
  const pocoCourtsCount = isPoco
    ? normalizePocoCourtsCount(detail.courtsCount)
    : null;

  return {
    ...detail,
    categoryGender,
    category,
    categoryMale,
    categoryFemale,
    super8Group,
    includeInRanking,
    description: String(detail.description || "").trim(),
    address: String(detail.address || "").trim(),
    city: String(detail.city || "").trim(),
    state: String(detail.state || "").trim(),
    shirtSizesEnabled: Boolean(detail.shirtSizesEnabled),
    shirtSizes: Boolean(detail.shirtSizesEnabled)
      ? normalizeTournamentShirtSizes(detail.shirtSizes)
      : [],
    shirtAvailabilityType:
      detail.shirtAvailabilityType === "period" ||
      detail.shirtAvailabilityType === "quantity"
        ? detail.shirtAvailabilityType
        : "none",
    shirtDeadlineAt: detail.shirtDeadlineAt || null,
    shirtQuantityLimit: detail.shirtQuantityLimit ?? null,
    shirtConfirmedCount: detail.shirtConfirmedCount || 0,
    shirtAvailableCount: detail.shirtAvailableCount ?? null,
    scheduledEndAt: detail.scheduledEndAt
      ? String(detail.scheduledEndAt).trim()
      : null,
    participantCpfs,
    participantsCount: Number(
      detail.participantsCount || participantCpfs.length,
    ),
    rounds: Array.isArray(detail.rounds) ? detail.rounds : [],
    roundsDetailed: Array.isArray(detail.roundsDetailed)
      ? detail.roundsDetailed
      : [],
    pairs: Array.isArray(detail.pairs) ? detail.pairs : [],
    participants: Array.isArray(detail.participants) ? detail.participants : [],
    registrationCategories,
    challengeModel:
      detail.challengeModel === "custom" ? "custom" : "tnp_standard",
    gameType:
      detail.gameType === "time" || detail.gameType === "games"
        ? detail.gameType
        : null,
    gameCount:
      Number.isFinite(Number(detail.gameCount)) && Number(detail.gameCount) > 0
        ? Math.floor(Number(detail.gameCount))
        : null,
    gameDurationMinutes:
      Number.isFinite(Number(detail.gameDurationMinutes)) &&
      Number(detail.gameDurationMinutes) > 0
        ? Math.floor(Number(detail.gameDurationMinutes))
        : null,
    courtsCount:
      pocoCourtsCount ??
      (Number.isFinite(Number(detail.courtsCount)) &&
        Number(detail.courtsCount) > 0
          ? Math.floor(Number(detail.courtsCount))
          : null),
    playersCount:
      pocoCourtsCount !== null
        ? pocoCourtsCount * 4
        : Number.isFinite(Number(detail.playersCount)) &&
            Number(detail.playersCount) > 0
          ? Math.floor(Number(detail.playersCount))
          : null,
  };
}
